import {
  type ComponentType,
  type ReactElement,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { cn } from '@/lib/cn';

export interface PopoverMenuItem {
  icon?: string | ComponentType<{ className?: string }>;
  label: ReactNode;
  onClick: () => void;
  value?: string;
  className?: string;
}

export interface PopoverMenuProps {
  button: ReactElement;
  items: PopoverMenuItem[];
  headerMenu?: ReactElement;
  placement?: 'top-start' | 'top-end' | 'bottom-start' | 'bottom-end';
  classes?: {
    container?: string;
    panel?: string;
  };
}

function getPanelPlacementClass(placement?: PopoverMenuProps['placement']) {
  switch (placement) {
    case 'top-end':
      return 'bottom-full right-0 mb-2';
    case 'bottom-start':
      return 'top-full left-0 mt-2';
    case 'bottom-end':
      return 'top-full right-0 mt-2';
    case 'top-start':
    default:
      return 'bottom-full left-0 mb-2';
  }
}

export const PopoverMenu = ({
  button,
  items,
  classes,
  headerMenu,
  placement,
}: PopoverMenuProps) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleDocumentMouseDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleDocumentMouseDown);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  return (
    <div
      ref={rootRef}
      className={cn(
        'relative',
        !classes?.container?.includes('mt-') && !classes?.container?.includes('m-') && 'mt-auto',
        !classes?.container?.includes('w-') && 'w-full',
        classes?.container,
      )}
    >
      <button
        type="button"
        className="relative w-full focus:outline-none"
        onClick={() => setOpen(current => !current)}
      >
        {button}
      </button>

      {open && (
        <div
          className={cn(
            'absolute z-50',
            getPanelPlacementClass(placement),
            !classes?.panel?.includes('w-') && 'w-full',
            !classes?.panel?.includes('p-') && 'p-2',
            classes?.panel,
          )}
          role="menu"
        >
          <div className="py-2 bg-white rounded border border-gray-200 shadow-lg">
            {headerMenu && headerMenu}
            {items.map(({ label, onClick, icon, className }, index) => {
              const Icon = typeof icon === 'string' ? null : icon;
              return (
                <button
                  key={index}
                  type="button"
                  onClick={event => {
                    event.stopPropagation();
                    event.preventDefault();
                    onClick();
                    setOpen(false);
                  }}
                  className={cn(
                    'w-full bg-white items-center text-gray-900 text-left text-[14px] leading-[20px] px-4 py-[6px]',
                    !className?.includes('block') && 'flex gap-3',
                    !className?.includes('hover:bg-') &&
                      !className?.includes('hover:!bg-') &&
                      'hover:bg-gray-900/12',
                    className,
                  )}
                >
                  {icon &&
                    (typeof icon === 'string' ? (
                      <img src={icon} alt="" className="h-6 w-6 min-h-6 min-w-6 shrink-0" />
                    ) : Icon ? (
                      <Icon className="h-4 w-4 shrink-0" />
                    ) : null)}
                  <div className="min-w-0 flex-1 text-[14px] leading-[20px] font-medium">
                    {label}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
